import { X } from "lucide-react";
import { Dialog as Primitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export const Dialog = Primitive.Root;
export const DialogTrigger = Primitive.Trigger;
export const DialogClose = Primitive.Close;

type DialogContentProps = Omit<ComponentProps<typeof Primitive.Content>, "title"> & {
  title: ReactNode;
  description?: ReactNode;
};

export function DialogContent({ className, title, description, children, ...props }: DialogContentProps) {
  const { t } = useTranslation();
  return (
    <Primitive.Portal>
      <Primitive.Overlay className="fixed inset-0 z-40 bg-black/50" />
      <Primitive.Content
        className={cn(
          "fixed top-1/2 left-1/2 z-50 flex max-h-[92dvh] w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border bg-surface shadow-2xl outline-none",
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
          <div className="min-w-0">
            <Primitive.Title className="text-base font-semibold">{title}</Primitive.Title>
            {description ? (
              <Primitive.Description className="mt-0.5 text-muted">{description}</Primitive.Description>
            ) : (
              <Primitive.Description className="sr-only">{title}</Primitive.Description>
            )}
          </div>
          <Primitive.Close
            aria-label={t("common.close")}
            className="-m-1 rounded-md p-1 text-muted hover:bg-surface-muted hover:text-fg"
          >
            <X className="size-5" aria-hidden="true" />
          </Primitive.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </Primitive.Content>
    </Primitive.Portal>
  );
}
